import { ToastrService } from 'ngx-toastr';
import { Globals } from '@theme/utils/globals';
import { Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EmitterService } from '@service/emitter/emitter.service';
import { ElectriciteService } from '@service/electricite/electricite.service';

export interface PreviewLine {
  ligne: number;
  contract: string;
  porte: string;
  debut: number;
  fin: number;
  date: string;
  statut: string;
  message: string;
  conso: number;
  montant: number;
  ancienMontant: number;
  loyerAvant: number;
  loyerApres: number;
  locataire: string;
  prix: number;
  rentUuid: string;
  editable: boolean;
  editing?: boolean;
}

@Component({
  selector: 'app-electricite-preview',
  templateUrl: './electricite-preview.component.html',
  styleUrls: ['./electricite-preview.component.scss']
})
export class ElectricitePreviewComponent implements OnInit {
  title: string = 'Preview Import Electricite';
  loading: boolean = false;
  submitting: boolean = false;
  previewData: PreviewLine[] = [];
  originalData: any = null;
  periode: string = '';

  // Stats
  totalNouveau: number = 0;
  totalMiseAJour: number = 0;
  totalErreur: number = 0;
  montantTotal: number = 0;

  global = { country: Globals.country, device: Globals.device };

  constructor(
    public modal: NgbActiveModal,
    public toastr: ToastrService,
    private emitter: EmitterService,
    private electriciteService: ElectriciteService
  ) {}

  ngOnInit(): void {
    this.calculateStats();
  }

  setPreviewData(data: PreviewLine[], originalData: any, periode: string): void {
    this.previewData = data;
    this.originalData = originalData;
    this.periode = periode;
    this.calculateStats();
  }

  calculateStats(): void {
    this.totalNouveau = this.previewData.filter(p => p.statut === 'NOUVEAU').length;
    this.totalMiseAJour = this.previewData.filter(p => p.statut === 'MISE_A_JOUR').length;
    this.totalErreur = this.previewData.filter(p => p.statut === 'ERREUR').length;
    this.montantTotal = this.previewData
      .filter(p => p.statut !== 'ERREUR')
      .reduce((sum, p) => sum + (p.montant || 0), 0);
  }

  getStatusBadgeClass(statut: string): string {
    switch (statut) {
      case 'NOUVEAU': return 'badge-success';
      case 'MISE_A_JOUR': return 'badge-warning';
      case 'ERREUR': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  editLine(line: PreviewLine): void {
    line.editing = true;
  }

  cancelEdit(line: PreviewLine): void {
    line.editing = false;
    // Recharger les donnees originales si necessaire
  }

  saveLine(line: PreviewLine): void {
    // Recalculer la consommation et le montant
    line.conso = line.fin - line.debut;
    if (line.prix) {
      line.montant = Math.round(line.conso * line.prix);
      line.loyerApres = line.loyerAvant - line.ancienMontant + line.montant;
    }
    line.editing = false;
    this.calculateStats();
  }

  removeLine(index: number): void {
    this.previewData.splice(index, 1);
    this.calculateStats();
  }

  onValidate(): void {
    const validLines = this.previewData.filter(p => p.statut !== 'ERREUR');

    if (validLines.length === 0) {
      this.toastr.error('Aucune ligne valide a importer', 'Erreur');
      return;
    }

    Swal.fire({
      title: 'Confirmation',
      text: `Voulez-vous enregistrer ${validLines.length} ligne(s) d'import ?`,
      icon: 'question',
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonText: 'Valider <i class="fas fa-check"></i>',
      cancelButtonText: 'Annuler <i class="feather icon-x-circle"></i>',
      confirmButtonColor: '#1bc943',
      reverseButtons: true
    }).then((result) => {
      if (!result.dismiss) {
        this.submitValidation();
      }
    });
  }

  submitValidation(): void {
    this.submitting = true;

    // Preparer les donnees pour l'API
    const options = this.previewData
      .filter(p => p.statut !== 'ERREUR')
      .map(p => ({
        contract: p.contract,
        porte: p.porte,
        debut: p.debut,
        fin: p.fin,
        conso: p.conso,
        date: p.date
      }));

    const payload = {
      options: options,
      periode: this.periode,
      date: this.originalData?.date || null
    };

    this.electriciteService.validatePreview(payload).subscribe(
      (res: any) => {
        this.submitting = false;
        if (res?.status === 'success') {
          this.toastr.success('Import electrique enregistre avec succes', 'Succes');
          this.emitter.emit({ action: 'ELECTRICITE_IMPORT', payload: res?.data });
          this.modal.close('ferme');
        }
      },
      (error) => {
        this.submitting = false;
        this.toastr.error('Erreur lors de l\'enregistrement', 'Erreur');
        console.error('Erreur validation preview:', error);
      }
    );
  }

  onClose(): void {
    this.modal.close('ferme');
  }
}
